import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LifeBuoy, AlertTriangle, Pill, Phone, User, Languages, Clock, WifiOff } from "lucide-react";
import axios from "axios";

/* Public emergency card viewer — the only page in the app that reads Health
   Vault data WITHOUT authentication. It hits /api/vault/emergency/{token}
   directly (bypassing our api.js token middleware) so first responders on any
   device can scan a QR and see the opted-in fields.
   The proxy layer only returns the fields the owner explicitly published.
   Cached by the service worker (offline-sw.js) so a first responder can still
   pull the card up on a weak-signal area if it has been viewed once online. */
export default function PublicEmergencyCard() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null, servedFromCache: false });
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/vault/emergency/${token}`)
      .then((r) => setState({ loading: false, data: r.data, error: null, servedFromCache: !navigator.onLine }))
      .catch((e) => setState({ loading: false, data: null,
        error: e?.response?.data?.detail || (!navigator.onLine
          ? "You appear to be offline and this emergency card has not been viewed on this device before."
          : "This card is not available."),
        servedFromCache: false,
      }));
  }, [token]);

  if (state.loading) return <div className="min-h-screen grid place-items-center bg-rose-50 text-rose-900">Loading emergency card…</div>;

  if (state.error) return (
    <div className="min-h-screen grid place-items-center bg-rose-50 p-6" data-testid="emergency-public-error">
      <div className="max-w-md text-center border border-rose-300 bg-white p-6 shadow-md">
        {!online ? <WifiOff className="w-10 h-10 mx-auto text-rose-600" /> : <LifeBuoy className="w-10 h-10 mx-auto text-rose-600" />}
        <h1 className="mt-3 font-heading font-900 text-xl text-rose-900">{!online ? "Offline — card not cached" : "Card unavailable"}</h1>
        <p className="mt-2 text-sm text-zinc-700">{state.error}</p>
        <p className="mt-3 text-[11px] text-muted-foreground">In an emergency, dial your local emergency number.</p>
      </div>
    </div>
  );

  const f = state.data.fields || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white p-4 sm:p-8" data-testid="emergency-public-card">
      {!online && (
        <div className="max-w-lg mx-auto mb-3 rounded bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900 flex items-center gap-2" data-testid="emergency-offline-banner">
          <WifiOff className="w-3.5 h-3.5 shrink-0" />
          <span><b>Offline snapshot.</b> You're viewing the last-synced copy of this emergency card. Data may be stale.</span>
        </div>
      )}
      <div className="max-w-lg mx-auto bg-white border-2 border-rose-600 shadow-xl">
        <div className="bg-rose-600 text-white px-5 py-4 flex items-center gap-3">
          <LifeBuoy className="w-8 h-8 shrink-0" />
          <div>
            <div className="font-heading font-900 text-xl">Emergency Health Card</div>
            <div className="text-xs text-rose-100">TravelerPulse.pro · My Social Pulse · read-only</div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {f.display_name && (
            <div className="flex items-center gap-2 text-lg"><User className="w-5 h-5 text-zinc-500" /><strong data-testid="ec-display-name">{f.display_name}</strong></div>
          )}
          {f.preferred_language && (
            <div className="flex items-center gap-2 text-sm text-zinc-700"><Languages className="w-4 h-4 text-zinc-500" /> Prefers <strong>{f.preferred_language}</strong></div>
          )}
          {f.blood_type && (
            <div className="border-l-4 border-rose-600 pl-3 py-1"><div className="text-[10px] font-800 uppercase tracking-widest text-zinc-500">Blood type</div><div className="font-mono text-2xl font-900" data-testid="ec-blood-type">{f.blood_type}</div></div>
          )}
          {Array.isArray(f.critical_allergies) && f.critical_allergies.length > 0 && (
            <div className="border border-rose-300 bg-rose-50 p-3" data-testid="ec-critical-allergies">
              <div className="text-[11px] font-800 uppercase tracking-widest text-rose-800 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Critical allergies</div>
              <ul className="mt-2 space-y-1 text-sm">
                {f.critical_allergies.map((a, i) => (
                  <li key={i} className="font-700 text-rose-900">{a.name}{a.reaction ? ` — ${a.reaction}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(f.current_medications) && f.current_medications.length > 0 && (
            <div className="border border-zinc-200 p-3" data-testid="ec-current-medications">
              <div className="text-[11px] font-800 uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Pill className="w-3.5 h-3.5" /> Current medications</div>
              <ul className="mt-2 space-y-1 text-sm">
                {f.current_medications.map((m, i) => (
                  <li key={i}>{m.name}{m.strength ? ` · ${m.strength}` : ""}{m.frequency ? ` · ${m.frequency}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {f.conditions && <div><div className="text-[11px] font-800 uppercase tracking-widest text-zinc-500">Conditions</div><p className="text-sm mt-0.5">{f.conditions}</p></div>}

          {(f.emergency_contact_name || f.emergency_contact_phone) && (
            <div className="border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[11px] font-800 uppercase tracking-widest text-emerald-800 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> In emergency, contact</div>
              <div className="mt-1 text-sm">
                {f.emergency_contact_name && <div className="font-700">{f.emergency_contact_name}</div>}
                {f.emergency_contact_phone && <a href={`tel:${f.emergency_contact_phone}`} className="text-emerald-800 font-700 underline">{f.emergency_contact_phone}</a>}
              </div>
            </div>
          )}
          {(f.physician_name || f.physician_phone) && (
            <div className="text-sm">
              <div className="text-[11px] font-800 uppercase tracking-widest text-zinc-500">Physician</div>
              {f.physician_name && <div>{f.physician_name}</div>}
              {f.physician_phone && <a href={`tel:${f.physician_phone}`} className="text-primary underline">{f.physician_phone}</a>}
            </div>
          )}
          {f.health_insurance && <div className="text-sm"><div className="text-[11px] font-800 uppercase tracking-widest text-zinc-500">Health insurance</div><div>{f.health_insurance}</div></div>}
          {f.travel_insurance && <div className="text-sm"><div className="text-[11px] font-800 uppercase tracking-widest text-zinc-500">Travel insurance</div><div>{f.travel_insurance}</div></div>}
          {f.instructions && <div className="text-sm"><div className="text-[11px] font-800 uppercase tracking-widest text-zinc-500">Instructions</div><p>{f.instructions}</p></div>}
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 text-[10px] text-zinc-500 bg-zinc-50 flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Generated {new Date(state.data.generated_at).toLocaleString()} · {state.data.notice}
        </div>
      </div>
    </div>
  );
}
